import React, { useCallback, useEffect, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import _debounce from 'lodash/debounce';
import { TranslateFunction } from 'react-localize-redux';
import { hashHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';

import { Currency } from '../../../core/api/general/general.types';
import {
  AvailabilityMode,
  Menu as MenuData,
  MenuSectionBehaviour,
} from '../../../core/api/menu/menu.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../core/api/restaurant/restaurant.types';
import { AppConfig } from '../../../core/api/whitelabel/whitelabel.types';
import { analyticsService } from '../../../core/services/analyticsService';
import { logger } from '../../../core/services/loggerService';
import {
  AddItemHookPayload,
  RemoveItemHookPayload,
} from '../../../core/types/basket.types';
import { ClickItemPayloadType } from '../../../core/types/menu.types';
import { Routes } from '../../../core/types/route.types';
import { NAV_BAR_HEIGHT } from '../../../pages/RestaurantMenu/RestaurantMenu';
import { changeUtils } from '../../../utils/changeUtils';
import { usePrevious } from '../../../utils/hooks/usePrevious';
import { useScroll } from '../../../utils/hooks/useScroll';
import { checkCountryCode } from '../../../utils/locationUtils';
import { handleScrollToSection } from '../../../utils/menuUtils';
import { urlUtils } from '../../../utils/urlUtils';
import * as BasketControl from '../../../weborder/BasketControl';
import { Utilities } from '../../../weborder/Utilities';
import { Snackbar } from '../../atoms/Snackbar/Snackbar';
import { InfoFaceIcon } from '../../icons/InfoFaceIcon';
import { Dialog } from '../../layouts/Dialog/Dialog';
import { Fallback } from '../../layouts/Fallback';
import { LoadingSpinner } from '../../layouts/LoadingSpinner/LoadingSpinner';
import { menuSectionUtils } from './menuSectionUtils';
import { MenuNavigation } from './partials/MenuNavigation/MenuNavigation';
import { MenuSearch } from './partials/MenuSearch';
import { MenuSection } from './partials/MenuSection/MenuSection';

interface Props {
  basket: BasketControl.Data;
  checkoutButton?: JSX.Element;
  configData: AppConfig;
  currency: Currency;
  expandedState: number[];
  isLoading: boolean;
  isStaticMenu?: boolean;
  languageCode: string;
  menuData: MenuData | null;
  onAddExpanded: (vrid: number, menuSectionId: number) => void;
  onAddItem: (payload: AddItemHookPayload) => void;
  onInitExpanded: (vrid: number, expandedState: number[]) => void;
  onItemClick: (payload: ClickItemPayloadType) => void;
  onRemoveExpanded: (vrid: number, menuSectionId: number) => void;
  onRemoveItem: (payload: RemoveItemHookPayload) => void;
  params?: Routes['params'];
  restaurantData: CollectionRestaurantDetail | DeliveryRestaurantDetail;
  setShowMenuSearch?: (showMenuSearch: boolean) => void;
  showMenuSearch?: boolean;
  translate: TranslateFunction;
  viewportTopOffset: number;
}

const useStyles = (isCheckoutButton: boolean) => () =>
  makeStyles((theme: Theme) => ({
    sectionsVisible: {
      position: 'relative',
      zIndex: 0,
    },

    sectionsHidden: {
      display: 'none',
    },

    menuSearch: {
      marginBottom: 0,
    },

    menuSearchDialog: {
      '& .MuiDialogContent-root': {
        backgroundColor: theme.palette.grey[200],
        padding: 0,
        marginBottom: isCheckoutButton ? theme.spacing(5.5) : 0,
      },
      '& .MuiTypography-root.MuiDialogTitle-root': {
        display: 'none',
      },
      '& .MuiDialogContent-root .MuiFormControl-root': {
        marginBottom: 0,
      },
      '& .fd-text__body1': {
        marginBottom: 0,
      },
    },

    [theme.breakpoints.up('sm')]: {
      message: {
        margin: theme.spacing(0, 3),
      },

      navigation: {
        '& .mobileNav': {
          padding: theme.spacing(0, 3),
        },
      },
    },

    [theme.breakpoints.up('md')]: {
      message: {
        margin: theme.spacing(0),
      },
    },
  }))();

const ACTIVATION_OFFSET_TRIGGER_HEIGHT = 100;
const MOBILE_OFFSET_TOP_FOR_MENU_SECTIONS = 121;

export function Menu(props: Props): JSX.Element {
  const {
    basket,
    checkoutButton,
    configData,
    currency,
    expandedState,
    isLoading,
    isStaticMenu = false,
    languageCode,
    menuData,
    onAddExpanded,
    onAddItem,
    onInitExpanded,
    onItemClick,
    onRemoveExpanded,
    onRemoveItem,
    params,
    restaurantData,
    setShowMenuSearch = (): void => {},
    showMenuSearch = false,
    translate,
    viewportTopOffset,
  } = props;

  const concessionStoreId = Number(params.concessionStoreId) || undefined;
  const vrId = Number(params.vrid);

  const theme = useTheme();
  const classes = useStyles(!!checkoutButton)();

  const { getAvailableMenuSections, getConcessionStoreAvailability } =
    menuSectionUtils;

  const [activeSection, setActiveSection] = useState(undefined);
  const [searchValue, setSearchValue] = useState('');
  const [availableMenuSections, setAvailableMenuSections] = useState([]);

  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('sm'));

  const { scrollOnTop } = useScroll();

  const isDesktopAndShowMenuSearch = !isMobileScreen && showMenuSearch;
  const isMobileAndShowMenuSearch = isMobileScreen && showMenuSearch;
  const isDesktopAndSearchValue = !isMobileScreen && !!searchValue.length;

  const hasConcessionStores = menuData?.ConcessionStores.length > 0;
  const navBarHeight = hasConcessionStores
    ? MOBILE_OFFSET_TOP_FOR_MENU_SECTIONS
    : NAV_BAR_HEIGHT;
  const firstMenuOptionHeight = isMobileScreen ? navBarHeight : 0;

  const previousConcessionId = usePrevious(params?.concessionStoreId);
  const hasNewConcessionStore =
    hasConcessionStores &&
    previousConcessionId !== undefined &&
    previousConcessionId !== params?.concessionStoreId;

  useEffect(() => {
    if (!menuData) {
      return;
    }

    let availableSections = getAvailableMenuSections(
      menuData.MenuSections,
      restaurantData.TimeZoneInfoId,
      concessionStoreId,
    );
    availableSections = changeUtils.sortBy(availableSections, 'DisplayOrder');

    if (availableSections.length) {
      setAvailableMenuSections(availableSections);
    } else {
      setAvailableMenuSections(null);
    }
  }, [menuData?.MenuId, concessionStoreId, restaurantData, menuData]);

  useEffect(() => {
    /**
     * This is fallback for handling `undefined` error faced
     * in photon websites. web-order should never be in a broken
     * state. It should always handle the exceptions and let user order.
     * restaurantData must have a Deliverytype 1/0 numeric.
     * If this does not exist that means the data is corrupted
     * and we need to redirect to the delivery/collection page.
     */
    if (restaurantData.DeliveryType === undefined) {
      const { type } = params;
      logger.error('UNDEFINED ERROR', {
        restaurantData,
        params,
        menuData,
        message: 'DeliveryType is undefined',
      });
      urlUtils.navigateToHash(`/${type}`);
    }
  }, []);

  useEffect(() => {
    if (availableMenuSections?.length > 0) {
      const [firstMenuSection] = availableMenuSections;
      setActiveSection(firstMenuSection.MenuSectionId);
    }
  }, [availableMenuSections?.length]);

  useEffect(() => {
    if (menuData) {
      setupExpandedStates();
    }
  }, [menuData?.MenuId]);

  useEffect(() => {
    if (
      scrollOnTop &&
      activeSection &&
      availableMenuSections?.length &&
      activeSection !== availableMenuSections[0].MenuSectionId
    ) {
      setActiveSection(availableMenuSections[0].MenuSectionId);
    }
  }, [scrollOnTop, activeSection, availableMenuSections?.length]);

  useEffect(() => {
    if (menuData?.MenuId && params?.concessionStoreId) {
      const isConcessionStoreAvailable = getConcessionStoreAvailability(
        params?.concessionStoreId,
        menuData?.MenuSections,
        restaurantData?.TimeZoneInfoId,
      );

      if (!isConcessionStoreAvailable) {
        hashHistory.replace(
          `/restaurant/${params.vrid}/${params.type}/${params.id}`,
        );
      }
    }
  }, [menuData?.MenuId, params?.concessionStoreId]);

  useEffect(() => {
    const scrollPageToConcessionMenus = (anchorPosition): void => {
      _debounce(() => {
        window.scrollTo({
          behavior: 'smooth',
          top: anchorPosition,
        });
      }, 300)();
    };

    const setFirstMenuSectionActive = (newTopSectionId): void => {
      _debounce(() => {
        setActiveSection(newTopSectionId);
      }, 1000)();
    };

    if (hasNewConcessionStore) {
      const concessionAnchorElement =
        document.getElementById('concessionAnchor');
      const anchorPosition =
        concessionAnchorElement.getBoundingClientRect().top + window.scrollY;
      scrollPageToConcessionMenus(anchorPosition);

      const newTopSectionId =
        availableMenuSections?.length &&
        availableMenuSections[0]?.MenuSectionId;
      setFirstMenuSectionActive(newTopSectionId);
    }
  }, [params?.concessionStoreId]);

  const checkSectionAvailabilityMode = (menuSection): boolean => {
    const {
      DisplayAlways,
      DisplayBasedOnTimes,
      DisplayAlwaysStartCollapsedBasedOnTimes,
    } = AvailabilityMode;
    const {
      AvailabilityMode: availabilityMode,
      AvailableTimes: availabilityTimes,
    } = menuSection.MenuSectionAvailability;
    const timezone = restaurantData.TimeZoneInfoId;

    switch (availabilityMode) {
      case DisplayAlways:
        return true;

      case DisplayBasedOnTimes: {
        return Utilities.isInPeriod(availabilityTimes, timezone);
      }

      case DisplayAlwaysStartCollapsedBasedOnTimes: {
        return Utilities.isInPeriod(availabilityTimes, timezone);
      }

      default:
        return false;
    }
  };

  const setupExpandedStates = (): void => {
    let expandedSections = [];

    menuData.MenuSections.forEach((menuSection) => {
      if (
        !menuSection.MenuSectionAvailability ||
        checkSectionAvailabilityMode(menuSection)
      ) {
        expandedSections.push(menuSection.MenuSectionId);
      }
    });

    if (menuData.MenuSectionBehaviour === MenuSectionBehaviour.ExpandSingle) {
      expandedSections = expandedSections.slice(0, 1);
    }

    onInitExpanded(vrId, expandedSections);
  };

  const handleTabClick = (sectionId: number): void => {
    const isExpandSingle =
      menuData?.MenuSectionBehaviour === MenuSectionBehaviour.ExpandSingle;

    if (isExpandSingle) {
      onInitExpanded(vrId, [sectionId]);
    } else {
      onAddExpanded(vrId, sectionId);
    }

    const targetId = `menuSection-${sectionId.toString()}`;
    _debounce(() => {
      handleScrollToSection(targetId, isMobileScreen, navBarHeight);
      setActiveMenuDebounced(sectionId);
    }, 300)();
  };

  const handleSetShowMenuSearch = (): void => {
    const eventData = {
      amp_label: 'Menu search clicked',
    };
    setShowMenuSearch(true);
    analyticsService.track(eventData);
  };

  const setActiveMenuDebounced = useCallback(
    _debounce((sectionId) => {
      setActiveSection(sectionId);
    }, 600),
    [],
  );

  if (availableMenuSections === null) {
    return (
      <Fallback
        dataFd="no-available-sections-fallback"
        description={translate('Menu_NoMenuSectionsAvailable') as string}
        icon={<InfoFaceIcon data-fd="info-face-icon" />}
        logMessage="No Menu Sections Available"
        variant="panel"
      />
    );
  }

  if (isLoading || !activeSection) {
    return <LoadingSpinner variant="panel" />;
  }

  const onMenuWaypointEnter = (menuSectionId, event, isFirst): void => {
    const sectionOffset =
      (isFirst ? firstMenuOptionHeight : navBarHeight) +
      viewportTopOffset +
      ACTIVATION_OFFSET_TRIGGER_HEIGHT;

    if (
      event.previousPosition !== 'above' ||
      event.waypointBottom > sectionOffset
    ) {
      return;
    }
    setActiveSection(menuSectionId);
  };

  const onMenuWaypointEnd = (menuSectionId, event, isFirst): void => {
    const sectionOffset =
      (isFirst ? firstMenuOptionHeight : navBarHeight) + viewportTopOffset;
    if (
      event.currentPosition === 'below' ||
      (event.waypointBottom > sectionOffset && event.waypointBottom < 0)
    ) {
      return;
    }
    setActiveSection(menuSectionId);
  };

  const renderMenuHeader = (): JSX.Element => {
    const displayMenuSectionNav = !!menuData.DisplaySectionLinks;

    return (
      <>
        {restaurantData.MenuMessage && (
          <div className={classes.message}>{renderMenuMessage()}</div>
        )}

        {!isDesktopAndShowMenuSearch && (
          <MenuNavigation
            activeMenuSectionId={activeSection}
            availableMenuSections={availableMenuSections}
            menu={menuData}
            openMenuSearch={handleSetShowMenuSearch}
            onTabClick={handleTabClick}
            params={params}
            showSectionLinks={displayMenuSectionNav}
            timezone={restaurantData.TimeZoneInfoId}
            translate={translate}
            viewportTopOffset={viewportTopOffset}
          />
        )}
      </>
    );
  };

  const renderMenuMessage = (): JSX.Element => {
    const menuMessageWithLineBreaks = Utilities.stringToJSX(
      restaurantData.MenuMessage.split('\n'),
      (part: string, index: number) => [part, <br key={`br-${index}`} />],
    );
    menuMessageWithLineBreaks.pop();

    return <Snackbar message={menuMessageWithLineBreaks} variant="info" />;
  };

  const renderMenuSections = (): JSX.Element[] => {
    const getExpandedState = (sectionId: number): boolean => {
      if (typeof expandedState === 'undefined') {
        return false;
      }

      return expandedState.some((section) => section === sectionId);
    };

    const menuSections: JSX.Element[] = availableMenuSections.map(
      (menuSection, index) => {
        const menuSectionData = {
          description: menuSection.Description,
          expandBehaviour: menuData.MenuSectionBehaviour,
          imageUrl: menuSection.ImageUrl,
          items: menuSection.MenuItems,
          name: menuSection.Name,
          sectionId: menuSection.MenuSectionId,
        };
        const isFirst = index === 0;
        const menuSectionId = menuSection.MenuSectionId;
        const isExpanded = getExpandedState(menuSectionId);

        const waypoint = (
          <Waypoint
            onEnter={(event): void =>
              onMenuWaypointEnter(menuSectionId, event, isFirst)
            }
            onLeave={(event): void =>
              onMenuWaypointEnd(menuSectionId, event, isFirst)
            }
            topOffset={viewportTopOffset + navBarHeight}
          />
        );

        const navBarHeightMenuSectionProp = menuData.DisplaySectionLinks
          ? navBarHeight
          : undefined;

        return (
          <MenuSection
            basket={basket}
            currency={currency}
            isExpanded={isExpanded}
            key={`menu-section-${menuSectionId}`}
            languageCode={languageCode}
            menuId={menuData.MenuId}
            menuSectionData={menuSectionData}
            navBarHeight={navBarHeightMenuSectionProp}
            onAddExpanded={onAddExpanded}
            onAddItem={onAddItem}
            onInitExpanded={onInitExpanded}
            onItemClick={onItemClick}
            onRemoveExpanded={onRemoveExpanded}
            onRemoveItem={onRemoveItem}
            translate={translate}
            vrId={vrId}
            waypoint={waypoint}
          />
        );
      },
    );

    return menuSections;
  };

  const renderMenuSearch = (): JSX.Element => (
    <MenuSearch
      basketItems={basket.items}
      closeMenuSearch={(): void => setShowMenuSearch(false)}
      currency={currency}
      languageCode={languageCode}
      menuSections={availableMenuSections}
      onAddItem={onAddItem}
      onItemClick={onItemClick}
      onRemoveItem={onRemoveItem}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      showTopBorder={!!menuData.DisplaySectionLinks}
      translate={translate}
    />
  );

  const isUKStore = checkCountryCode(configData, 'GB');

  const isMenuDisplayReady =
    (hasConcessionStores && concessionStoreId) || !hasConcessionStores;

  const sectionsStyles = !isDesktopAndSearchValue
    ? classes.sectionsVisible
    : `${classes.sections} ${classes.sectionsHidden}`;

  return (
    <div className={classes.menu} id="concessionAnchor">
      {!isStaticMenu && renderMenuHeader()}

      {isDesktopAndShowMenuSearch && (
        <div className={classes.menuSearch}>{renderMenuSearch()}</div>
      )}

      {isMenuDisplayReady && (
        <div className={sectionsStyles} data-fd="menu-content" role="tablist">
          {renderMenuSections()}
        </div>
      )}

      {isUKStore && (
        <Snackbar
          className={classes.calorieAdvice}
          message={translate('Menu_calories_info') as string}
          variant="info"
        />
      )}
      {isMobileAndShowMenuSearch && (
        <Dialog
          actions={checkoutButton}
          className={classes.menuSearchDialog}
          open
        >
          {renderMenuSearch()}
        </Dialog>
      )}
    </div>
  );
}
